import {
    Badge,
    BlockStack,
    Card,
    Form,
    Page,
    TextField,
    Button,
    Checkbox,
    InlineStack,
    Toast,
    Spinner,
    Banner,
    SkeletonDisplayText,
} from "@shopify/polaris";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangleIcon, LockIcon } from "@shopify/polaris-icons";

import { MultiSelectCombobox } from "../components/atoms/MultiSelectCombobox";
import {
    useUpdateExportRuleMutation,
    useGetExportRuleByIdQuery,
    useGetStaffUsersQuery,
} from "../store/store/slices/ExportRTK";
import { EXPORT_FIELD_MAPPING } from "../utils";

type FormValues = {
    ruleName: string;
    columns: string[];
    staff: string[];
    emailReceivers: string[];
    notifyAdmin: boolean;
    exportRangeDays: number
};

export default function ExportRulesEdit() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [toastMessage, setToastMessage] = useState<{
        msg: string;
        error: boolean;
    } | null>(null);

    /* ----------------------------
       FETCH RULE
    -----------------------------*/
    const {
        data: ruleResponse,
        isLoading: isRuleLoading,
        isError,
    } = useGetExportRuleByIdQuery(id!, {
        skip: !id,
    });

    /* ----------------------------
       FETCH STAFF
    -----------------------------*/
    const { data: staffData } = useGetStaffUsersQuery();

    const staffOptions = useMemo(
        () =>
            staffData?.data?.map((user: any) => ({
                label: `${user.first_name} ${user.last_name}`,
                value: user._id,
            })) || [],
        [staffData]
    );

    const staffIdToName = useMemo(() => {
        const map: Record<string, string> = {};
        staffOptions.forEach((s) => {
            map[s.value] = s.label;
        });
        return map;
    }, [staffOptions]);

    /* ----------------------------
       UPDATE MUTATION
    -----------------------------*/
    const [
        updateExportRule,
        { isLoading: isUpdating },
    ] = useUpdateExportRuleMutation();

    /* ----------------------------
       FORM
    -----------------------------*/
    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            ruleName: "",
            columns: [],
            staff: [],
            emailReceivers: [],
            notifyAdmin: true,
        },
    });

    /* ----------------------------
       REVERSE MAP
    -----------------------------*/
    const reverseColumnMap = useMemo(() => {
        const reversed: Record<string, string> = {};
        Object.entries(EXPORT_FIELD_MAPPING).forEach(
            ([label, dbField]) => {
                reversed[dbField] = label;
            }
        );
        return reversed;
    }, []);

    /* ----------------------------
       PREFILL FORM
    -----------------------------*/
    useEffect(() => {
        if (ruleResponse?.data) {
            const rule = ruleResponse.data;

            reset({
                ruleName: rule.name,
                columns:
                    rule.columns
                        ?.map((dbField: string) => reverseColumnMap[dbField])
                        ?.filter(Boolean) || [],
                staff: rule.users || [],
                emailReceivers:
                    rule.emailReceivers?.map((u: any) => u._id) || [],
                notifyAdmin: rule.notifyAdmin ?? true,
                exportRangeDays: rule.exportRangeDays
            });
        }
    }, [ruleResponse, reset, reverseColumnMap]);

    /* ----------------------------
       TOAST
    -----------------------------*/
    const showToast = (msg: string, error: boolean = true) =>
        setToastMessage({ msg, error });

    /* ----------------------------
       SUBMIT
    -----------------------------*/
    const onSubmit = async (formData: FormValues) => {
        if (!formData.ruleName.trim())
            return showToast("Rule name is required");

        if (!formData.columns.length)
            return showToast("At least one column is required");

        if (!formData.staff.length)
            return showToast("At least one staff user is required");
        if (!formData.exportRangeDays)
            return showToast("Data export days range is required");


        try {
            const mappedColumns = formData.columns.map(
                (col) => EXPORT_FIELD_MAPPING[col]
            );

            await updateExportRule({
                id,
                name: formData.ruleName,
                columns: mappedColumns,
                users: formData.staff,
                emailReceivers: formData.emailReceivers,
                notifyAdmin: formData.notifyAdmin,
                exportRangeDays: formData.exportRangeDays
            }).unwrap();

            showToast("Export rule updated successfully 🎉", false);

            setTimeout(() => {
                navigate("/dashboard/export-rules");
            }, 1000);
        } catch (error: any) {
            console.log("")
        }
    };



    /* ----------------------------
       RENDER
    -----------------------------*/
    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <Page
                narrowWidth
                title="Edit Export Rule"
                backAction={{
                    onAction: () =>
                        navigate(-1),
                }}
                titleMetadata={
                    <Badge tone="success-strong" icon={LockIcon}>
                        Admin only
                    </Badge>
                }
            >

                <Card>
                    <BlockStack gap="400">
                        {!isError && <Banner tone="info" icon={AlertTriangleIcon}>
                            Update export rule settings below.
                        </Banner>}
                        {!isRuleLoading && !isError ? <>
                            <Controller
                                name="ruleName"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        label="Rule name"
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={isUpdating}
                                    />
                                )}
                            />

                            <Controller
                                name="exportRangeDays"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        label="Number of days the staff can export data within"
                                        autoComplete="off"
                                        value={field.value}
                                        type="number"
                                        onChange={field.onChange}
                                    />
                                )}
                            />



                            <Controller
                                name="columns"
                                control={control}
                                render={({ field }) => (
                                    <MultiSelectCombobox
                                        options={Object.keys(
                                            EXPORT_FIELD_MAPPING
                                        ).map((col) => ({
                                            label: col,
                                            value: col,
                                        }))}
                                        selected={field.value}
                                        onChange={field.onChange}
                                        label="Select columns"
                                    />
                                )}
                            />

                            <Controller
                                name="staff"
                                control={control}
                                render={({ field }) => (
                                    <MultiSelectCombobox
                                        options={staffOptions}
                                        selected={field.value}
                                        onChange={field.onChange}
                                        label="Staff who can export"
                                        tagRenderer={(val: string) =>
                                            staffIdToName[val]
                                        }

                                    />
                                )}
                            />

                            <Controller
                                name="emailReceivers"
                                control={control}
                                render={({ field }) => (
                                    <MultiSelectCombobox
                                        options={staffOptions}
                                        selected={field.value}
                                        onChange={field.onChange}
                                        label="Email receivers"
                                        tagRenderer={(val: string) =>
                                            staffIdToName[val]
                                        }

                                    />
                                )}
                            />

                            <Controller
                                name="notifyAdmin"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        label="Notify admin"
                                        checked={field.value}
                                        onChange={field.onChange}
                                        disabled={isUpdating}
                                    />
                                )}
                            />

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "end",
                                }}
                            >
                                <Button
                                    submit
                                    variant="primary"
                                    loading={isUpdating}
                                >
                                    Update Rule
                                </Button>
                            </div>
                        </> : isError ?
                            <Banner icon={AlertTriangleIcon} tone="critical">Export rule could not be found!</Banner>
                            : <BlockStack gap={"600"} align="end">
                                <SkeletonDisplayText maxWidth="100%" size="extraLarge" />
                                <SkeletonDisplayText maxWidth="100%" size="extraLarge" />
                                <SkeletonDisplayText maxWidth="100%" size="extraLarge" />
                                <SkeletonDisplayText maxWidth="100%" size="extraLarge" />
                                <SkeletonDisplayText maxWidth="100%" size="extraLarge" />
                                <SkeletonDisplayText maxWidth="100%" size="large" />
                            </BlockStack>}
                    </BlockStack>
                </Card>
            </Page>

            {toastMessage && (
                <Toast
                    content={toastMessage.msg}
                    error={toastMessage.error}
                    onDismiss={() => setToastMessage(null)}
                />
            )}
        </Form>
    );
}